/**
 * @file src/context/SocketContext.jsx
 * @description
 * Single, app-wide Socket.IO connection to the notifications service
 * (reached through the gateway origin at path `/socket.io`). Opens ONE
 * socket while the session holds a usable access token, tears it down on
 * logout, and reconnects when the token rotates.
 *
 * The server emits `new_notification` with a payload of:
 *   { id, event, title, body, type, priority, metadata }
 * For chat messages `event === 'CHAT_MESSAGE'` and `metadata` carries
 * `{ threadId, messageId, senderId, subjectType, subjectId, projectId,
 *    preview, title, action_url }`.
 *
 * Consumers subscribe via `useSocketEvent(handler)` (fires for every
 * `new_notification`) or read `connected` from `useSocket()`.
 *
 * Design notes:
 *   - Mounted OUTSIDE AuthContext (see main.jsx), so the token is read
 *     straight from `storage` rather than a hook. A light interval polls
 *     the token so login / logout / silent-refresh rotations are picked
 *     up without a page reload.
 *   - Everything is defensive: if the socket can't reach the gateway it
 *     simply never flips `connected` to true and existing REST polling
 *     elsewhere keeps the UI working.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { storage } from '../utils/storage';
import MoksyLogger from '../utils/logger';

// Derive the gateway origin from the axios base URL by stripping the
// trailing `/api/vN` segment. e.g. http://localhost:5001/api/v1 →
// http://localhost:5001. Socket.IO then connects to origin + `/socket.io`.
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001/api/v1';
const SOCKET_ORIGIN = API_BASE.replace(/\/api\/v\d+\/?$/, '').replace(/\/+$/, '');

// How often we re-check the stored token for login / logout / refresh.
const TOKEN_POLL_MS = 4000;

const SocketContext = createContext({
  connected: false,
  subscribe: () => () => {},
  subscribeTyping: () => () => {},
  subscribeRead: () => () => {},
  emitTyping: () => {},
  emitRead: () => {},
  queryPresence: () => Promise.resolve({})
});

// How long we wait for a presence-query ACK before assuming "all offline".
const PRESENCE_ACK_TIMEOUT_MS = 3000;

export const SocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const handlersRef = useRef(new Set());
  const typingHandlersRef = useRef(new Set());
  const readHandlersRef = useRef(new Set());
  const tokenRef = useRef(null);

  // Register a handler for incoming `new_notification` payloads. Returns
  // an unsubscribe fn. Stable identity so effects don't re-subscribe.
  const subscribe = useCallback((handler) => {
    if (typeof handler !== 'function') return () => {};
    handlersRef.current.add(handler);
    return () => { handlersRef.current.delete(handler); };
  }, []);

  // Register a handler for inbound ephemeral `chat:typing` relays. Payload
  // is `{ threadId, from }`. Returns an unsubscribe fn; stable identity.
  const subscribeTyping = useCallback((handler) => {
    if (typeof handler !== 'function') return () => {};
    typingHandlersRef.current.add(handler);
    return () => { typingHandlersRef.current.delete(handler); };
  }, []);

  // Fire-and-forget "I'm typing" signal for a thread. `toUserIds` is the
  // set of *other* participants to notify. No-op when the socket is down.
  const emitTyping = useCallback((threadId, toUserIds) => {
    const socket = socketRef.current;
    const to = Array.isArray(toUserIds) ? toUserIds.filter(Boolean).map(String) : [];
    if (!socket || !socket.connected || !to.length) return;
    try { socket.emit('chat:typing', { threadId: threadId || null, to }); }
    catch { /* best effort — typing is disposable */ }
  }, []);

  // Register a handler for inbound `chat:read` relays. Payload is
  // `{ threadId, from, at }` — the counterpart telling us they've read the
  // thread. Returns an unsubscribe fn; stable identity (mirrors typing).
  const subscribeRead = useCallback((handler) => {
    if (typeof handler !== 'function') return () => {};
    readHandlersRef.current.add(handler);
    return () => { readHandlersRef.current.delete(handler); };
  }, []);

  // Fire-and-forget "I've read this thread" receipt. `toUserIds` is the set
  // of *other* participants to notify (server relays a `chat:read` to them).
  // No-op when the socket is down.
  const emitRead = useCallback((threadId, toUserIds) => {
    const socket = socketRef.current;
    const to = Array.isArray(toUserIds) ? toUserIds.filter(Boolean).map(String) : [];
    if (!socket || !socket.connected || !to.length) return;
    try { socket.emit('chat:read', { threadId: threadId || null, to, at: new Date().toISOString() }); }
    catch { /* best effort — read receipts are disposable */ }
  }, []);

  // On-demand presence lookup. Resolves `{ [userId]: boolean }` from the
  // server's ACK. Resolves `{}` (treat as all offline) if the socket is
  // down or the ACK doesn't land within the timeout — never rejects, so
  // callers can `.then` without a guard.
  const queryPresence = useCallback((userIds) => {
    const socket = socketRef.current;
    const ids = Array.isArray(userIds) ? userIds.filter(Boolean).map(String) : [];
    if (!socket || !socket.connected || !ids.length) return Promise.resolve({});
    return new Promise((resolve) => {
      let settled = false;
      const done = (states) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(states && typeof states === 'object' ? states : {});
      };
      const timer = setTimeout(() => done({}), PRESENCE_ACK_TIMEOUT_MS);
      try {
        socket.emit('chat:presence:query', { userIds: ids }, (resp) => {
          done(resp && resp.states ? resp.states : {});
        });
      } catch {
        done({});
      }
    });
  }, []);

  useEffect(() => {
    let disposed = false;

    const teardown = () => {
      if (socketRef.current) {
        try {
          socketRef.current.removeAllListeners();
          socketRef.current.disconnect();
        } catch { /* best effort */ }
        socketRef.current = null;
      }
      if (!disposed) setConnected(false);
    };

    const connect = (token) => {
      teardown();
      // A missing bearer token is no longer a reason not to dial. Sessions are
      // httpOnly cookies, `storage.KEYS.ACCESS_TOKEN` is never written, and
      // gating on it meant this client never once attempted a connection —
      // so the socket silently did nothing while the UI said "Syncing"
      // forever. `withCredentials` sends the cookie on the handshake and the
      // server now reads it; the token is passed when we happen to have one.
      if (!SOCKET_ORIGIN) return;
      try {
        const socket = io(SOCKET_ORIGIN, {
          auth: token ? { token } : {},
          path: '/socket.io',
          transports: ['websocket', 'polling'],
          autoConnect: true,
          reconnection: true,
          withCredentials: true
        });
        socketRef.current = socket;

        socket.on('connect', () => { if (!disposed) setConnected(true); });
        socket.on('disconnect', () => { if (!disposed) setConnected(false); });
        // connect_error fires on handshake/proxy failure — stay quiet and
        // let REST polling elsewhere carry the UI. socket.io keeps retrying.
        socket.on('connect_error', () => { if (!disposed) setConnected(false); });

        socket.on('new_notification', (payload) => {
          handlersRef.current.forEach((handler) => {
            try { handler(payload); } catch (error) {
              MoksyLogger.error('SOCKET.HANDLER_FAILED', error);
            }
          });
        });

        // A notification closed on the server because the task behind it is done
        // (a job claimed, a verdict submitted). Same subscribers as a new one, so
        // the bell re-syncs and stops showing it without a reload. The payload is
        // marked `resolved` so a subscriber that announces new items can skip it.
        socket.on('notifications_resolved', (payload) => {
          const marked = { ...(payload && typeof payload === 'object' ? payload : {}), resolved: true };
          handlersRef.current.forEach((handler) => {
            try { handler(marked); } catch (error) {
              MoksyLogger.error('SOCKET.HANDLER_FAILED', error);
            }
          });
        });

        // Ephemeral typing relay from the notifications gateway. Fanned out
        // to any `useTypingEvent` subscribers; never persisted.
        socket.on('chat:typing', (payload) => {
          typingHandlersRef.current.forEach((handler) => {
            try { handler(payload); } catch (error) {
              MoksyLogger.error('SOCKET.TYPING_HANDLER_FAILED', error);
            }
          });
        });

        // Read-receipt relay (`{ threadId, from, at }`) — the counterpart
        // telling us they've read the thread. Fanned out to `useReadEvent`
        // subscribers so the open conversation can flip Sent → Seen live.
        socket.on('chat:read', (payload) => {
          readHandlersRef.current.forEach((handler) => {
            try { handler(payload); } catch (error) {
              MoksyLogger.error('SOCKET.READ_HANDLER_FAILED', error);
            }
          });
        });
      } catch (error) {
        MoksyLogger.error('SOCKET.CONNECT_FAILED', error);
        teardown();
      }
    };

    // Reconnect only when the usable token actually changes.
    const syncToken = () => {
      const token = storage.hasUsableAccessToken()
        ? storage.get(storage.KEYS.ACCESS_TOKEN)
        : null;
      if (token === tokenRef.current) return;
      tokenRef.current = token;
      connect(token);
    };

    // Dial once on mount regardless. `syncToken` only reconnects when the
    // token CHANGES, and for a cookie session it is null forever — so on its
    // own it never fired a first connection at all.
    tokenRef.current = storage.hasUsableAccessToken()
      ? storage.get(storage.KEYS.ACCESS_TOKEN)
      : null;
    connect(tokenRef.current);
    const interval = setInterval(syncToken, TOKEN_POLL_MS);

    return () => {
      disposed = true;
      clearInterval(interval);
      teardown();
    };
  }, []);

  const value = useMemo(
    () => ({ connected, subscribe, subscribeTyping, subscribeRead, emitTyping, emitRead, queryPresence }),
    [connected, subscribe, subscribeTyping, subscribeRead, emitTyping, emitRead, queryPresence]
  );

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);

/**
 * Subscribe a handler to every incoming `new_notification` payload for
 * the lifetime of the calling component. The latest handler closure is
 * always used, so callers don't need to memoize it.
 */
export const useSocketEvent = (handler) => {
  const { subscribe } = useSocket();
  const savedRef = useRef(handler);
  useEffect(() => { savedRef.current = handler; }, [handler]);
  useEffect(() => subscribe((payload) => savedRef.current?.(payload)), [subscribe]);
};

/**
 * Subscribe a handler to every inbound ephemeral `chat:typing` relay
 * (`{ threadId, from }`) for the lifetime of the calling component. Mirrors
 * `useSocketEvent` — the latest closure is always used, no memoization
 * needed. No-op-safe when the socket is down.
 */
export const useTypingEvent = (handler) => {
  const { subscribeTyping } = useSocket();
  const savedRef = useRef(handler);
  useEffect(() => { savedRef.current = handler; }, [handler]);
  useEffect(() => subscribeTyping((payload) => savedRef.current?.(payload)), [subscribeTyping]);
};

/**
 * Subscribe a handler to every inbound `chat:read` relay
 * (`{ threadId, from, at }`) for the lifetime of the calling component.
 * Mirrors `useTypingEvent` — the latest closure is always used, no
 * memoization needed. No-op-safe when the socket is down.
 */
export const useReadEvent = (handler) => {
  const { subscribeRead } = useSocket();
  const savedRef = useRef(handler);
  useEffect(() => { savedRef.current = handler; }, [handler]);
  useEffect(() => subscribeRead((payload) => savedRef.current?.(payload)), [subscribeRead]);
};
